import { useEffect, useState } from "react";
import { X, Loader2, MessageCircle, Phone, Mail, MapPin, Send, AlertTriangle, Pencil, Clock, Calculator } from "lucide-react";
import { toast } from "sonner";
import { api } from "../../lib/api";
import { styleFor } from "../../lib/leadKindStyles";
import { formatPhoneDisplay, telLink, whatsappLink, isValidPhoneNumber, countryOf } from "../../lib/phone";
import { formatReceivedAt } from "../../lib/dateFormat";
import PhoneInputE164 from "../PhoneInputE164";
import RecommendedTutorsPanel from "./RecommendedTutorsPanel";
import LeadLifecyclePanel from "./LeadLifecyclePanel";
import LeadTimeline from "./LeadTimeline";
import AdminTutorDetailPanel from "./AdminTutorDetailPanel";
import WhatsAppReplyPanel from "./WhatsAppReplyPanel";
import LeadShareButton from "./LeadShareButton";
import { resolveStateFromCity } from "../../data/indianCityToState";

const STATUSES = {
    leads: [
        { v: "new", l: "New", c: "bg-amber-100 text-amber-800 border-amber-200" },
        { v: "contacted", l: "Contacted", c: "bg-blue-100 text-blue-700 border-blue-200" },
        { v: "in_progress", l: "In Progress", c: "bg-indigo-100 text-indigo-700 border-indigo-200" },
        { v: "converted", l: "Converted", c: "bg-emerald-100 text-emerald-700 border-emerald-200" },
        { v: "lost", l: "Lost", c: "bg-slate-100 text-slate-600 border-slate-200" },
    ],
    tutors: [
        { v: "new", l: "New", c: "bg-amber-100 text-amber-800 border-amber-200" },
        { v: "under_review", l: "Under Review", c: "bg-blue-100 text-blue-700 border-blue-200" },
        { v: "approved", l: "Approved", c: "bg-emerald-100 text-emerald-700 border-emerald-200" },
        { v: "rejected", l: "Rejected", c: "bg-red-100 text-red-700 border-red-200" },
    ],
};

export default function LeadDetailModal({ kind, item, onClose, onSaved, onOpened }) {
    const isTutor = kind === "tutor-applications";
    const statuses = isTutor ? STATUSES.tutors : STATUSES.leads;
    const path = kind;

    const [status, setStatus] = useState(item.status || "new");
    const [assignedTo, setAssignedTo] = useState(item.assigned_to || "");
    const [rejectionReason, setRejectionReason] = useState(item.rejection_reason || "");
    const [note, setNote] = useState("");
    const [saving, setSaving] = useState(false);
    const [notesLog, setNotesLog] = useState(item.notes_log || []);
    const [phoneDraft, setPhoneDraft] = useState(item.phone || "");
    const [editingPhone, setEditingPhone] = useState(false);
    const [savingPhone, setSavingPhone] = useState(false);
    const [phoneE164, setPhoneE164] = useState(item.phone || "");
    const [phoneCountry, setPhoneCountry] = useState(item.phone_country || countryOf(item.phone) || null);
    const [phoneMissing, setPhoneMissing] = useState(!!item.phone_country_missing || !isValidPhoneNumber(item.phone || ""));
    // V154 — Track opened state so the modal header can show "Received: … · Opened: …"
    const [openedAt, setOpenedAt] = useState(item.opened_at || null);

    // V154 — Auto mark-read on first open. Idempotent server-side, so this is
    // safe to run every time; we only fire when the item is currently unread
    // AND not a tutor application (only demo-request kinds are read-tracked
    // in this rollout). onSaved bubbles the change so parent lists refresh.
    useEffect(() => {
        if (isTutor) return;
        if (openedAt) return;
        let cancelled = false;
        (async () => {
            try {
                const r = await api.post(`/admin/${path}/${item.id}/mark-opened`);
                if (cancelled) return;
                const stamp = r?.data?.opened_at || new Date().toISOString();
                setOpenedAt(stamp);
                // V170.5 — server auto-flips status "new" → "contacted" on
                // first open for inquiries/registrations. Sync local state so
                // the details view stops showing "NEW" immediately.
                const newStatus = r?.data?.status;
                if (newStatus && newStatus !== status) {
                    setStatus(newStatus);
                }
                // Optimistic parent-list patch: flip the just-opened row to
                // the "read" visual immediately. We do NOT call onSaved() here
                // — a full refetch would race the DB write and clobber the
                // optimistic patch (the listing aggregation may not yet
                // reflect the write). The next natural refresh (status/note
                // save, filter change, background poll) will pick up any
                // other server-side changes.
                try { onOpened?.(item.id, stamp, newStatus); } catch (_e) { /* ignore */ }
            } catch (_e) {
                // Silent — mark-opened is a background convenience, not a
                // blocker for viewing the lead. If it fails once we'll retry
                // on the next open naturally.
            }
        })();
        return () => { cancelled = true; };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [item.id, path, isTutor]);

    const savePhone = async () => {
        if (!isValidPhoneNumber(phoneDraft)) {
            toast.error("Please enter a valid phone number with country code.");
            return;
        }
        setSavingPhone(true);
        try {
            const r = await api.patch(`/admin/leads/${path}/${item.id}/phone`, { phone: phoneDraft });
            const next = r.data.phone || phoneDraft;
            setPhoneE164(next);
            setPhoneCountry(r.data.phone_country || countryOf(next));
            setPhoneMissing(false);
            setEditingPhone(false);
            toast.success("Phone updated");
            onSaved?.();
        } catch (e) {
            toast.error(e.response?.data?.detail || "Failed to update phone");
        } finally {
            setSavingPhone(false);
        }
    };

    const save = async () => {
        const payload = {};
        if (status !== item.status) payload.status = status;
        if (!isTutor && assignedTo !== (item.assigned_to || "")) payload.assigned_to = assignedTo;
        if (isTutor && status === "rejected" && rejectionReason && rejectionReason !== (item.rejection_reason || "")) {
            payload.rejection_reason = rejectionReason;
        }
        if (note.trim()) payload.note = note.trim();
        if (Object.keys(payload).length === 0) {
            toast.info("No changes to save.");
            return;
        }
        setSaving(true);
        try {
            const r = await api.patch(`/admin/${path}/${item.id}`, payload);
            setNotesLog(r.data.notes_log || notesLog);
            setNote("");
            toast.success("Updated");
            onSaved?.();
        } catch {
            toast.error("Failed to update");
        } finally {
            setSaving(false);
        }
    };

    const fields = buildDisplayFields(item);

    return (
        <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center bg-black/50 p-0 sm:p-4" onClick={onClose}>
            <div
                className="bg-white rounded-t-2xl sm:rounded-2xl max-w-3xl w-full h-[100dvh] sm:h-auto sm:max-h-[90vh] overflow-y-auto pb-32 sm:pb-0"
                onClick={(e) => e.stopPropagation()}
                data-testid="lead-detail-modal"
                data-kind={kind}
            >
                {(() => { const ks = styleFor(kind); return (
                <div className={`p-6 border-b border-slate-200 ${ks.leftBorder} flex items-center justify-between sticky top-0 bg-white z-10`}>
                    <div className="flex items-start gap-3">
                        <span
                            data-testid={`lead-detail-kind-chip-${kind}`}
                            className={`shrink-0 mt-1 inline-flex items-center text-[10px] px-2 py-0.5 rounded font-bold uppercase tracking-wider ${ks.badge}`}
                        >
                            {ks.short}
                        </span>
                        <div>
                            <div className="text-xs uppercase tracking-[0.2em] text-slate-400">{isTutor ? "Tutor Application" : kind === "registrations" ? "Demo Registration" : "Inquiry"}</div>
                            <h3 className="font-heading text-xl font-bold text-[#0F172A]">{item.name}</h3>
                            {item.created_at && (
                                <div className="mt-1 flex flex-wrap items-center gap-x-3 gap-y-0.5 text-[11px] text-slate-500" data-testid="lead-received-line">
                                    <span className="inline-flex items-center gap-1">
                                        <Clock className="w-3 h-3" />
                                        Received: <span className="font-semibold text-slate-700">{formatReceivedAt(item.created_at)}</span>
                                    </span>
                                    {openedAt && (
                                        <span className="inline-flex items-center gap-1 text-emerald-600" data-testid="lead-opened-line">
                                            · Opened: <span className="font-semibold">{formatReceivedAt(openedAt)}</span>
                                        </span>
                                    )}
                                </div>
                            )}
                        </div>
                    </div>
                    <button onClick={onClose} className="p-1 hover:bg-slate-100 rounded">
                        <X className="w-5 h-5" />
                    </button>
                </div>
                ); })()}

                {/* quick actions */}
                <div className="px-6 py-4 bg-slate-50 border-b border-slate-200 flex flex-wrap items-center gap-2 text-sm" data-testid="lead-detail-actions">
                    {editingPhone ? (
                        <div className="flex items-center gap-2 w-full sm:w-auto" data-testid="lead-detail-phone-editor">
                            <div className="flex-1 min-w-[240px]">
                                <PhoneInputE164 value={phoneDraft} onChange={setPhoneDraft} testid="lead-detail-phone-input" />
                            </div>
                            <button
                                onClick={savePhone}
                                disabled={savingPhone || !isValidPhoneNumber(phoneDraft)}
                                data-testid="lead-detail-phone-save"
                                className="px-3 py-2 bg-[#1E3A8A] text-white text-xs font-semibold rounded-lg disabled:opacity-60"
                            >
                                {savingPhone ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : "Save"}
                            </button>
                            <button
                                onClick={() => { setEditingPhone(false); setPhoneDraft(phoneE164); }}
                                className="px-3 py-2 bg-white border border-slate-200 text-xs font-semibold rounded-lg"
                            >
                                Cancel
                            </button>
                        </div>
                    ) : (
                        <>
                            {phoneE164 && (
                                <a
                                    href={telLink(phoneE164)}
                                    data-testid="lead-call"
                                    className="inline-flex items-center gap-1.5 bg-white border border-slate-200 px-3 py-1.5 rounded-lg hover:border-blue-300"
                                >
                                    <Phone className="w-3.5 h-3.5" /> {formatPhoneDisplay(phoneE164)}
                                </a>
                            )}
                            {phoneE164 && isValidPhoneNumber(phoneE164) && (
                                <a
                                    href={whatsappLink(phoneE164)}
                                    target="_blank"
                                    rel="noreferrer"
                                    data-testid="lead-whatsapp"
                                    className="inline-flex items-center gap-1.5 bg-[#25D366] text-white px-3 py-1.5 rounded-lg hover:opacity-90"
                                >
                                    <MessageCircle className="w-3.5 h-3.5" /> WhatsApp
                                </a>
                            )}
                            <button
                                onClick={() => { setPhoneDraft(phoneE164); setEditingPhone(true); }}
                                data-testid="lead-detail-phone-edit"
                                title={phoneMissing ? "Country code missing — please assign" : "Edit phone (E.164)"}
                                className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold border ${
                                    phoneMissing
                                        ? "bg-amber-50 border-amber-300 text-amber-800 hover:bg-amber-100"
                                        : "bg-white border-slate-200 text-slate-600 hover:border-blue-300"
                                }`}
                            >
                                <Pencil className="w-3 h-3" /> {phoneMissing ? "Assign country code" : "Edit"}
                            </button>
                            {phoneCountry && (
                                <span className="inline-flex items-center text-[10px] px-1.5 py-0.5 rounded bg-slate-100 text-slate-600 font-semibold uppercase tracking-wider" data-testid="lead-detail-phone-country">
                                    {phoneCountry}
                                </span>
                            )}
                            {item.email && (
                                <a
                                    href={`mailto:${item.email}`}
                                    data-testid="lead-email"
                                    className="inline-flex items-center gap-1.5 bg-white border border-slate-200 px-3 py-1.5 rounded-lg hover:border-blue-300"
                                >
                                    <Mail className="w-3.5 h-3.5" /> {item.email}
                                </a>
                            )}
                            {!isTutor && (
                                <LeadShareButton item={item} kind={kind} variant="compact" />
                            )}
                            {kind === "registrations" && (
                                <button type="button" data-testid="lead-fee-rec-btn"
                                    onClick={() => window.dispatchEvent(new CustomEvent("fee-rec:open", { detail: {
                                        lead_id: item.id, parent_name: item.parent_name || item.name || "", student_name: item.parent_name ? (item.name || "") : "",
                                        phone: item.phone || "", city: item.city || "", grade: item.grade || "", board: item.board || "", subject: item.subjects || "",
                                    } }))}
                                    className="inline-flex items-center gap-1.5 bg-white border border-slate-200 px-3 py-1.5 rounded-lg hover:border-blue-300 text-[#1E3A8A] font-semibold">
                                    <Calculator className="w-3.5 h-3.5" /> Generate Fee Recommendation
                                </button>
                            )}
                            {item.city && (
                                <span className="inline-flex items-center gap-1.5 text-slate-500 px-2 py-1.5">
                                    <MapPin className="w-3.5 h-3.5" /> {item.city}
                                </span>
                            )}
                        </>
                    )}
                </div>

                <div className="p-6 grid md:grid-cols-2 gap-6">
                    {/* Left: details */}
                    <div className="space-y-2 text-sm">
                        <div className="text-xs font-semibold uppercase tracking-[0.15em] text-slate-500 mb-2">Submission Details</div>
                        <div className="grid grid-cols-3 gap-2 py-1 border-b border-slate-100">
                            <div className="text-xs uppercase tracking-wider text-slate-500">phone</div>
                            <div className="col-span-2 text-slate-800 break-words font-mono" data-testid="lead-detail-phone-text">
                                {phoneE164 ? formatPhoneDisplay(phoneE164) : "—"}
                                {phoneCountry && <span className="ml-2 text-[10px] uppercase tracking-wider text-slate-500">[{phoneCountry}]</span>}
                            </div>
                        </div>
                        {fields.map(([k, v]) => (
                            <div key={k} className="grid grid-cols-3 gap-2 py-1 border-b border-slate-100 last:border-0">
                                <div className="text-xs uppercase tracking-wider text-slate-500">{k.replace(/_/g, " ")}</div>
                                <div className="col-span-2 text-slate-800 break-words">
                                    {renderLeadFieldValue(k, v)}
                                </div>
                            </div>
                        ))}
                    </div>

                    {/* Right: workflow */}
                    <div className="space-y-4">
                        <div data-testid="lead-status-select">
                            <label className="text-xs font-semibold uppercase tracking-[0.15em] text-slate-500 mb-2 block">Status</label>
                            <div className="flex flex-wrap gap-1.5">
                                {statuses.map((s) => (
                                    <button
                                        key={s.v}
                                        onClick={() => setStatus(s.v)}
                                        data-testid={`lead-status-${s.v}`}
                                        className={`px-3 py-1.5 rounded-lg text-xs font-semibold border transition-all ${
                                            status === s.v ? `${s.c} ring-2 ring-offset-1 ring-[#1E3A8A]` : "bg-white border-slate-200 text-slate-600 hover:border-slate-300"
                                        }`}
                                    >
                                        {s.l}
                                    </button>
                                ))}
                            </div>
                        </div>

                        {!isTutor && (
                            <div>
                                <label className="text-xs font-semibold uppercase tracking-[0.15em] text-slate-500 mb-2 block">Assigned To</label>
                                <input
                                    value={assignedTo}
                                    onChange={(e) => setAssignedTo(e.target.value)}
                                    placeholder="Coordinator name"
                                    data-testid="lead-assigned-to"
                                    className="w-full px-3 py-2 text-sm rounded-lg border border-slate-200 focus:outline-none focus:ring-2 focus:ring-[#3B82F6]"
                                />
                            </div>
                        )}

                        {isTutor && status === "rejected" && (
                            <div>
                                <label className="text-xs font-semibold uppercase tracking-[0.15em] text-red-600 mb-2 flex items-center gap-1">
                                    <AlertTriangle className="w-3.5 h-3.5" /> Rejection Reason
                                </label>
                                <textarea
                                    value={rejectionReason}
                                    onChange={(e) => setRejectionReason(e.target.value)}
                                    rows={2}
                                    data-testid="tutor-rejection-reason"
                                    placeholder="Why was the application rejected?"
                                    className="w-full px-3 py-2 text-sm rounded-lg border border-red-200 bg-red-50 focus:outline-none focus:ring-2 focus:ring-red-300"
                                />
                            </div>
                        )}

                        <div>
                            <label className="text-xs font-semibold uppercase tracking-[0.15em] text-slate-500 mb-2 block">Add Note</label>
                            <textarea
                                value={note}
                                onChange={(e) => setNote(e.target.value)}
                                rows={3}
                                data-testid="lead-note-input"
                                placeholder="What happened in the last call / interaction?"
                                className="w-full px-3 py-2 text-sm rounded-lg border border-slate-200 focus:outline-none focus:ring-2 focus:ring-[#3B82F6]"
                            />
                        </div>

                        <button
                            onClick={save}
                            disabled={saving}
                            data-testid="lead-save"
                            className="w-full inline-flex items-center justify-center gap-2 px-4 py-2.5 bg-[#1E3A8A] text-white font-semibold rounded-lg hover:bg-[#1E40AF] disabled:opacity-60"
                        >
                            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
                            {saving ? "Saving…" : "Save & Log"}
                        </button>
                    </div>
                </div>

                {/* V42 — Recommended Tutors panel is now scoped to the Lead
                    Pipeline → Demo Posting stage ONLY. On the initial Demo
                    Registration view (pipeline_stage === "leads") the panel,
                    AI Re-rank, filters, match % and Assign actions are
                    intentionally hidden so the screen focuses on demo details
                    and assignment history. The AI engine is still available
                    server-side and re-appears the moment an admin moves the
                    lead into the "demo" (Demo Posting) stage. */}
                {kind === "registrations" && (
                    <div className="px-6 pb-6">
                        <LeadLifecyclePanel leadId={item.id} lead={item} onChanged={() => onSaved?.()} />
                    </div>
                )}
                {kind === "registrations" && (item.pipeline_stage === "demo") && (
                    <div className="px-6 pb-6" data-testid="lead-detail-recommended-tutors">
                        <RecommendedTutorsPanel leadId={item.id} lead={item} onAssigned={() => onSaved?.()} />
                    </div>
                )}

                {/* V25 Batch B #15 — Full tutor detail (Aadhaar, login history, referrals) */}
                {isTutor && (
                    <div className="px-6 pb-6">
                        <AdminTutorDetailPanel
                            applicationId={item.id}
                            onAadhaarChanged={() => onSaved?.()}
                            onClose={onClose}
                            onDeleted={() => onSaved?.()}
                        />
                    </div>
                )}

                {/* Pipeline timeline — only for DEMO leads (registrations) */}
                {kind === "registrations" && (
                    <div className="px-6 pb-6">
                        <LeadTimeline leadId={item.id} />
                    </div>
                )}

                {/* V138 — WhatsApp Reply Generator (Demo Registrations + Inquiries only) */}
                {(kind === "registrations" || kind === "inquiries") && (
                    <WhatsAppReplyPanel
                        kind={kind}
                        item={{ ...item, phone: phoneE164 || item.phone }}
                        onLogged={(nextLog) => setNotesLog(nextLog || notesLog)}
                    />
                )}

                {/* Notes timeline */}
                <div className="px-6 pb-6">
                    <div className="text-xs font-semibold uppercase tracking-[0.15em] text-slate-500 mb-3">Activity Log ({notesLog.length})</div>
                    {notesLog.length === 0 ? (
                        <div className="text-sm text-slate-400 italic">No notes yet — add the first one above.</div>
                    ) : (
                        <div className="space-y-2">
                            {[...notesLog].reverse().map((n, i) => {
                                const isWaSent = n.kind === "wa_sent";
                                return (
                                <div key={i}
                                    data-testid={isWaSent ? "activity-log-wa-sent" : "activity-log-note"}
                                    className={`border rounded-lg p-3 text-sm ${isWaSent ? "bg-emerald-50/60 border-emerald-200" : "bg-slate-50 border-slate-200"}`}>
                                    <div className="flex items-center justify-between text-xs text-slate-500 mb-1 gap-2 flex-wrap">
                                        <span className="font-semibold inline-flex items-center gap-1.5">
                                            {isWaSent && (
                                                <span className="inline-flex items-center gap-1 text-[9px] uppercase tracking-[0.15em] font-bold text-emerald-800 bg-emerald-100 rounded-full px-1.5 py-0.5">
                                                    <MessageCircle className="w-2.5 h-2.5" /> Sent on WA
                                                </span>
                                            )}
                                            {n.by}
                                        </span>
                                        <span>{new Date(n.at).toLocaleString()}{n.status ? ` · ${n.status}` : ""}</span>
                                    </div>
                                    {isWaSent && (n.template_label || n.language_label) && (
                                        <div className="text-[10px] uppercase tracking-wider text-emerald-800 font-semibold mb-1">
                                            {n.template_label}{n.language_label ? ` · ${n.language_label}` : ""}
                                        </div>
                                    )}
                                    <div className={`whitespace-pre-wrap ${isWaSent ? "text-slate-800" : "text-slate-700"}`}>{n.note}</div>
                                </div>
                            );})}
                        </div>
                    )}
                </div>

                {/* V21 — Mobile sticky bottom action bar (visible only on small screens) */}
                {!isTutor && (
                    <div
                        className="sm:hidden fixed bottom-0 left-0 right-0 bg-white border-t border-slate-200 px-2 py-2 z-50 shadow-[0_-4px_12px_rgba(0,0,0,0.08)] safe-area-pad"
                        data-testid="lead-sticky-actions"
                    >
                        <div className="grid grid-cols-4 gap-1">
                            {isValidPhoneNumber(phoneE164) && (
                                <a
                                    href={telLink(phoneE164)}
                                    data-testid="lead-action-call"
                                    className="flex flex-col items-center gap-0.5 px-1 py-2 rounded-lg active:bg-blue-50 text-blue-700"
                                >
                                    <Phone className="w-5 h-5" />
                                    <span className="text-[10px] font-semibold">Call</span>
                                </a>
                            )}
                            {isValidPhoneNumber(phoneE164) && (
                                <a
                                    href={whatsappLink(phoneE164)}
                                    target="_blank"
                                    rel="noreferrer"
                                    data-testid="lead-action-whatsapp"
                                    className="flex flex-col items-center gap-0.5 px-1 py-2 rounded-lg active:bg-emerald-50 text-emerald-700"
                                >
                                    <MessageCircle className="w-5 h-5" />
                                    <span className="text-[10px] font-semibold">WhatsApp</span>
                                </a>
                            )}
                            <LeadShareButton item={item} kind={kind} variant="iconOnly" />
                            {item.email && (
                                <a
                                    href={`mailto:${item.email}`}
                                    data-testid="lead-action-email"
                                    className="flex flex-col items-center gap-0.5 px-1 py-2 rounded-lg active:bg-amber-50 text-amber-700"
                                >
                                    <Mail className="w-5 h-5" />
                                    <span className="text-[10px] font-semibold">Email</span>
                                </a>
                            )}
                            <button
                                type="button"
                                onClick={() => {
                                    const el = document.querySelector('[data-testid="lead-status-select"]');
                                    el?.scrollIntoView({ behavior: "smooth", block: "center" });
                                    el?.focus?.();
                                }}
                                data-testid="lead-action-status"
                                className="flex flex-col items-center gap-0.5 px-1 py-2 rounded-lg active:bg-indigo-50 text-indigo-700"
                            >
                                <AlertTriangle className="w-5 h-5" />
                                <span className="text-[10px] font-semibold">Status</span>
                            </button>
                            <button
                                type="button"
                                onClick={() => {
                                    document.querySelector('[data-testid="recommended-tutors-panel"]')?.scrollIntoView({ behavior: "smooth", block: "start" });
                                }}
                                data-testid="lead-action-assign"
                                className="flex flex-col items-center gap-0.5 px-1 py-2 rounded-lg active:bg-purple-50 text-purple-700"
                            >
                                <Send className="w-5 h-5" />
                                <span className="text-[10px] font-semibold">Assign</span>
                            </button>
                            <button
                                type="button"
                                onClick={() => setEditingPhone(true)}
                                data-testid="lead-action-edit"
                                className="flex flex-col items-center gap-0.5 px-1 py-2 rounded-lg active:bg-slate-100 text-slate-700"
                            >
                                <Pencil className="w-5 h-5" />
                                <span className="text-[10px] font-semibold">Edit</span>
                            </button>
                            <button
                                type="button"
                                onClick={() => {
                                    const el = document.querySelector('[data-testid="lead-note-input"]');
                                    el?.scrollIntoView({ behavior: "smooth", block: "center" });
                                    el?.focus?.();
                                }}
                                data-testid="lead-action-followup"
                                className="flex flex-col items-center gap-0.5 px-1 py-2 rounded-lg active:bg-rose-50 text-rose-700"
                            >
                                <MapPin className="w-5 h-5" />
                                <span className="text-[10px] font-semibold">Note</span>
                            </button>
                            <button
                                type="button"
                                onClick={onClose}
                                data-testid="lead-action-close"
                                className="flex flex-col items-center gap-0.5 px-1 py-2 rounded-lg active:bg-slate-100 text-slate-500"
                            >
                                <X className="w-5 h-5" />
                                <span className="text-[10px] font-semibold">Close</span>
                            </button>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
}

function renderLeadFieldValue(key, v) {
    if (v === null || v === undefined || v === "") return "—";
    if (key === "created_at" || key === "updated_at" || key.endsWith("_at")) {
        try { return new Date(v).toLocaleString(); } catch { /* ignore */ }
    }
    if (key === "additional_students" && Array.isArray(v)) {
        if (!v.length) return "—";
        return (
            <ul className="space-y-1 text-xs">
                {v.map((s, i) => (
                    <li key={i} className="bg-slate-50 rounded px-2 py-1 border border-slate-200">
                        <strong>{s?.name || `Student ${i + 2}`}</strong>
                        {s?.grade && <span className="text-slate-500"> · {s.grade}</span>}
                        {s?.board && <span className="text-slate-500"> · {s.board}</span>}
                        {s?.subjects && <span className="text-slate-500"> · {s.subjects}</span>}
                        {s?.preferred_tutor_gender && s.preferred_tutor_gender !== "any" && (
                            <span className="text-slate-500"> · {s.preferred_tutor_gender}</span>
                        )}
                    </li>
                ))}
            </ul>
        );
    }
    if (key === "attribution" && v && typeof v === "object") {
        // V139 — Pretty-print UTM / landing metadata instead of raw JSON.
        const rows = [
            ["Landing page", v.landing_path],
            ["Page category", v.landing_page_category],
            ["Source", v.utm_source || v.source],
            ["Medium", v.utm_medium || v.medium],
            ["Campaign", v.utm_campaign || v.campaign],
            ["Content", v.utm_content],
            ["Term", v.utm_term],
            ["Referrer", v.referrer],
            ["Gclid", v.gclid],
            ["Fbclid", v.fbclid],
        ].filter(([, val]) => val !== undefined && val !== null && String(val).trim() !== "");
        if (!rows.length) return "—";
        return (
            <ul className="space-y-0.5 text-xs">
                {rows.map(([k2, val]) => (
                    <li key={k2}>
                        <span className="text-slate-500">{k2}:</span>{" "}
                        <span className="text-slate-800 break-all">{String(val)}</span>
                    </li>
                ))}
            </ul>
        );
    }
    if (Array.isArray(v)) {
        return v.length ? v.join(", ") : "—";
    }
    if (typeof v === "object") {
        try { return <code className="text-[10px]">{JSON.stringify(v)}</code>; } catch { return "—"; }
    }
    if (typeof v === "boolean") return v ? "Yes" : "No";
    return String(v);
}

/* ================================================================== */
/* V139 — Submission-only view                                        */
/*                                                                    */
/*   • Enrich the record so missing state/country auto-populate from  */
/*     the parent's selected city (client-side fallback for legacy    */
/*     records where the form auto-map wasn't yet in place).          */
/*   • Hide truly-empty fields (null / undefined / '' / whitespace /  */
/*     [] / {}). Booleans and numbers ALWAYS show — they represent    */
/*     an explicit selection.                                         */
/*   • Hide pure-system fields (ids, pipeline internals, migration    */
/*     flags, form auto-fill trackers) that the parent never touched. */
/*   • Group + order fields so parent-visible data comes first, then  */
/*     preferences, then attribution / system metadata.               */
/* ================================================================== */

// Fields hidden entirely from the "Submission Details" panel: either
// rendered elsewhere (phone / status / notes) or purely internal.
const HIDDEN_KEYS = new Set([
    "id", "_id", "status", "assigned_to", "notes_log", "rejection_reason",
    "updated_at", "phone", "phone_country", "phone_country_inferred",
    "phone_country_missing", "phone_e164_migrated",
    // Form-state helpers that occasionally leak through
    "_country_autofill", "_phone_country_iso",
    // Internal system IDs — text twins (`board`, `curriculum`) are shown
    "board_id", "curriculum_id", "foreign_country_code",
    // Pipeline internals — visible in dedicated panels
    "pipeline_migrated_at",
    // Machine copy of the human-friendly preferred_demo_ist_formatted
    "preferred_demo_ist_iso",
]);

// Display order for parent-facing fields; anything not listed falls to
// the tail in original insertion order so we never accidentally hide a
// custom field.
const FIELD_ORDER = [
    // Identity
    "parent_name", "name", "email",
    // Student profile
    "grade", "board", "curriculum", "school_name", "subjects",
    "wants_all_major_subjects",
    // Message / requirement
    "message",
    // Mode + location
    "mode", "country", "state", "city", "pin_code", "permanent_address",
    // Preferences
    "preferred_tutor_gender", "language_preference", "curriculum_preference",
    "exam_goal", "learning_level", "preferred_days", "preferred_time_window",
    "class_duration_minutes", "special_requirements",
    // Foreign / NRI demo window
    "preferred_demo_country_name", "preferred_demo_country_code",
    "preferred_demo_date", "preferred_demo_local_12h", "preferred_demo_time",
    "preferred_demo_tz", "preferred_demo_ist_formatted",
    // Additional students
    "additional_students",
    // Attribution + system
    "attribution", "lead_tag", "pipeline_stage", "pipeline_sub_status",
    "created_at",
];

function _isEmpty(v) {
    if (v === null || v === undefined) return true;
    if (typeof v === "string") return v.trim() === "" || v.trim() === "—";
    if (Array.isArray(v)) return v.length === 0;
    if (typeof v === "object") return Object.keys(v).length === 0;
    return false;
}

function _enrichLead(item) {
    // Fill state/country from city when missing (client-side fallback for
    // legacy Indian records saved before the form auto-map was in place).
    const out = { ...item };
    if (_isEmpty(out.state) && !_isEmpty(out.city)) {
        const resolved = resolveStateFromCity(out.city);
        if (resolved) out.state = resolved;
    }
    if (_isEmpty(out.country) && !_isEmpty(out.state)) {
        // resolveStateFromCity only yields Indian states → safe to infer India.
        out.country = "India";
    }
    return out;
}

function buildDisplayFields(item) {
    const enriched = _enrichLead(item);
    // Drop hidden + empty keys.
    const entries = Object.entries(enriched).filter(([k, v]) => {
        if (HIDDEN_KEYS.has(k)) return false;
        if (_isEmpty(v)) return false;
        return true;
    });
    // Stable ordered result.
    const order = new Map(FIELD_ORDER.map((k, i) => [k, i]));
    entries.sort((a, b) => {
        const ai = order.has(a[0]) ? order.get(a[0]) : 999 + Object.keys(item).indexOf(a[0]);
        const bi = order.has(b[0]) ? order.get(b[0]) : 999 + Object.keys(item).indexOf(b[0]);
        return ai - bi;
    });
    return entries;
}

